import { apiClient } from './client';

export interface CallingAgentUser {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  gender: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  profile: string | null;
  status: string;
  data_details: any;
}

export interface CallingAgent {
  id: number;
  uuid: string;
  user_id: number | null;
  type: string;
  display_name: string;
  description: string;
  voice_provider: string;
  voice_id: string;
  language: string;
  assistant_id: string | null;
  voice_details?: VapiVoice[];
  prompt: string;
  settings: {
    first_message?: string;
    [key: string]: unknown;
  };
  data_details: any;
  created_by: number | null;
  updated_by: number | null;
  deleted_by: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user?: CallingAgentUser;
}

export interface CreateCallingAgentRequest {
  type: string;
  data_details?: Record<string, unknown>;
  display_name: string;
  description?: string;
  voice_provider: string;
  voice_id: string;
  language: string;
  prompt: string;
  settings: {
    first_message: string;
    [key: string]: unknown;
  };
}

export interface UpdateCallingAgentRequest {
  type?: string;
  data_details?: Record<string, unknown>;
  display_name?: string;
  description?: string;
  voice_provider?: string;
  voice_id?: string;
  language?: string;
  prompt?: string;
  settings?: {
    first_message?: string;
    [key: string]: unknown;
  };
}

export interface CallingAgentListRequest {
  page?: number;
  page_size?: number;
  filters?: {
    city?: string;
    state?: string;
    country?: string;
    language?: string;
    voice_provider?: string;
  };
  search?: string;
  sort?: Record<string, 'asc' | 'desc'>;
}

export interface CallingAgentPrerequisites {
  voice_providers: string[];
  languages: string[];
}

export interface VapiVoice {
  id: string;
  provider: string;
  providerId: string;
  name: string;
  gender?: string;
  accent?: string;
  previewUrl?: string;
  description?: string;
}

export interface VapiVoicesRequest {
  provider?: string;
  language?: string;
  gender?: string;
  search?: string;
  limit?: number;
  page?: number;
}

export interface CallingAgentDropdownOption {
  label: string;
  value: string;
}

export interface CallingAgentCallHistory {
  id: number;
  uuid: string;
  calling_agent_id: number | null;
  calling_agent_uuid: string | null;
  vapi_call_id: string | null;
  assistant_id: string | null;
  phone: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_details: Record<string, unknown> | null;
  status: string | null;
  direction: string | null;
  ended_reason: string | null;
  duration_seconds: number | string | null;
  cost: string | number | null;
  transcript: string | null;
  summary: string | null;
  recording_url: string | null;
  stereo_recording_url: string | null;
  metadata: Record<string, unknown> | null;
  request_payload: Record<string, unknown> | null;
  response_payload: Record<string, unknown> | null;
  webhook_payload: Record<string, unknown> | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  callingAgent?: Pick<
    CallingAgent,
    'uuid' | 'display_name' | 'type' | 'voice_provider' | 'voice_id' | 'assistant_id'
  > | null;
  lead_id?: number | null;
  lead_details?: Record<string, unknown> | null;
  campaign_id?: number | null;
  campaign_details?: Record<string, unknown> | null;
}

export interface CallingAgentCallHistoryListRequest {
  page?: number;
  page_size?: number;
  filters?: {
    calling_agent_uuid?: string;
    vapi_call_id?: string;
    assistant_id?: string;
    phone?: string;
    contact_name?: string;
    contact_email?: string;
    status?: string;
    direction?: string;
    created_from?: string;
    created_to?: string;
    dateRangeType?: 'dateRange' | 'lastxdays';
    startDate?: string;
    endDate?: string;
    day?: number;
  };
  search?: string;
  sort?: Record<string, 'asc' | 'desc'>;
}

class CallingAgentsService {
  private readonly basePath = '/calling-agents';
  private readonly clientBasePath = '/clients/calling-agents';

  async prerequisites(): Promise<any> {
    return apiClient.get(`${this.basePath}/pre-requisites`);
  }

  async vapiVoices(params: VapiVoicesRequest = {}): Promise<any> {
    return apiClient.get(`${this.basePath}/vapi/voices`, { params });
  }

  async create(data: CreateCallingAgentRequest): Promise<any> {
    return apiClient.post(this.basePath, data);
  }

  async getById(uuid: string): Promise<any> {
    return apiClient.get(`${this.basePath}/${uuid}`);
  }

  async listing(params: CallingAgentListRequest = {}): Promise<any> {
    return apiClient.post(`${this.basePath}/listing`, params);
  }

  async dropdown(): Promise<any> {
    return apiClient.get(`${this.clientBasePath}/dropdown`);
  }

  async callHistoryListing(
    params: CallingAgentCallHistoryListRequest = {},
  ): Promise<any> {
    return apiClient.post(`${this.basePath}/call-history/listing`, params);
  }

  async update(uuid: string, data: UpdateCallingAgentRequest): Promise<any> {
    return apiClient.put(`${this.basePath}/${uuid}`, data);
  }

  async remove(uuid: string): Promise<any> {
    return apiClient.delete(`${this.basePath}/${uuid}`);
  }
}

export const callingAgentsService = new CallingAgentsService();
